import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post } from '../lib/api.js';

interface CasbEvent {
  id: string;
  platform: string;
  event_type: string;
  severity: string;
  user_email: string;
  user_display_name: string;
  description: string;
  detected_at: string;
  status: string;
  raw_data?: Record<string, unknown>;
  resolution_note?: string;
}

interface EventsResponse {
  data: CasbEvent[];
  hasMore?: boolean;
}

const PLATFORM_ICONS: Record<string, string> = {
  google_workspace: '🔵',
  slack: '💬',
  dropbox: '📦',
  salesforce: '☁️',
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  suspicious_signin: 'Suspicious Sign-in',
  failed_login_spike: 'Failed Login Spike',
  mfa_disabled: 'MFA Disabled',
  admin_role_granted: 'Admin Role Granted',
  password_changed: 'Password Changed',
  session_from_new_location: 'New Location Login',
  bulk_download: 'Bulk Download',
  bulk_delete: 'Bulk Delete',
  external_share: 'External Share',
  public_share: 'Public Share',
  sensitive_file_accessed: 'Sensitive File Access',
  oauth_app_authorized: 'OAuth App Authorized',
  oauth_app_scopes_elevated: 'Scopes Elevated',
  third_party_app_installed: 'App Installed',
  api_key_created: 'API Key Created',
  security_setting_changed: 'Security Setting Changed',
  forwarding_rule_created: 'Forwarding Rule',
  dlp_policy_violated: 'DLP Violation',
  sharing_policy_changed: 'Sharing Policy Changed',
};

const PLATFORMS = [
  { value: '', label: 'All Platforms' },
  { value: 'google_workspace', label: 'Google Workspace' },
  { value: 'slack', label: 'Slack' },
  { value: 'dropbox', label: 'Dropbox' },
  { value: 'salesforce', label: 'Salesforce' },
];

const SEVERITIES = [
  { value: '', label: 'All Severities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'investigating', label: 'Investigating' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'false_positive', label: 'False Positive' },
];

export function CasbEventsPage() {
  const [platform, setPlatform] = useState('');
  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<CasbEvent | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const params = new URLSearchParams();
  if (platform) params.set('platform', platform);
  if (severity) params.set('severity', severity);
  if (status) params.set('status', status);

  const { data: eventsData, loading, error, refresh } = useApi<EventsResponse>(`/protect/casb/events?${params.toString()}`);
  const events = eventsData?.data ?? [];

  async function handleAction(eventId: string, action: string) {
    setActionLoading(eventId);
    try {
      await post(`/protect/casb/events/${eventId}?action=${action}`, { note: note || undefined });
      setNote('');
      setSelectedEvent(null);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(null);
    }
  }

  function severityBadge(sev: string) {
    const colors =
      sev === 'critical' ? 'bg-red-100 text-red-700' :
      sev === 'high' ? 'bg-orange-100 text-orange-700' :
      sev === 'medium' ? 'bg-yellow-100 text-yellow-700' :
      'bg-emerald-100 text-emerald-700';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors} capitalize`}>{sev}</span>;
  }

  function statusBadge(st: string) {
    const colors =
      st === 'new' ? 'bg-red-100 text-red-700' :
      st === 'investigating' ? 'bg-yellow-100 text-yellow-700' :
      st === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
      'bg-gray-100 text-gray-600';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors} capitalize`}>{st.replace('_', ' ')}</span>;
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading CASB events...</div></div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">CASB Events</h2>
      <p className="text-gray-600 mb-6">Security events across all connected cloud platforms.</p>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <select value={platform} onChange={e => setPlatform(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm">
          {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select value={severity} onChange={e => setSeverity(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm">
          {SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm">
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {/* Events Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Platform</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Description</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">User</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Severity</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Time</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No CASB events found. Connect a platform to start monitoring.</td></tr>
            ) : events.map((e) => (
              <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedEvent(e)}>
                <td className="px-4 py-3">{PLATFORM_ICONS[e.platform] ?? e.platform}</td>
                <td className="px-4 py-3 text-gray-600">{EVENT_TYPE_LABELS[e.event_type] ?? e.event_type}</td>
                <td className="px-4 py-3 font-medium text-gray-900 max-w-xs truncate">{e.description}</td>
                <td className="px-4 py-3 text-gray-600">{e.user_display_name || e.user_email}</td>
                <td className="px-4 py-3">{severityBadge(e.severity)}</td>
                <td className="px-4 py-3">{statusBadge(e.status)}</td>
                <td className="px-4 py-3 text-gray-500">{new Date(e.detected_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-right" onClick={ev => ev.stopPropagation()}>
                  {e.status === 'new' && (
                    <button onClick={() => handleAction(e.id, 'investigate')} disabled={actionLoading === e.id} className="text-xs text-blue-600 hover:text-blue-800 mr-2 disabled:opacity-50">
                      Investigate
                    </button>
                  )}
                  {(e.status === 'new' || e.status === 'investigating') && (
                    <button onClick={() => handleAction(e.id, 'resolve')} disabled={actionLoading === e.id} className="text-xs text-emerald-600 hover:text-emerald-800 disabled:opacity-50">
                      Resolve
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Event Detail Slide-over */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/30" onClick={() => setSelectedEvent(null)} />
          <div className="ml-auto w-full max-w-lg bg-white shadow-xl h-full overflow-y-auto relative">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Event Details</h3>
                <button onClick={() => setSelectedEvent(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Platform</p>
                  <p className="font-medium">{PLATFORM_ICONS[selectedEvent.platform]} {selectedEvent.platform.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Event Type</p>
                  <p className="font-medium">{EVENT_TYPE_LABELS[selectedEvent.event_type] ?? selectedEvent.event_type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Description</p>
                  <p className="font-medium">{selectedEvent.description}</p>
                </div>
                <div className="flex gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Severity</p>
                    {severityBadge(selectedEvent.severity)}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    {statusBadge(selectedEvent.status)}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">User</p>
                  <p className="font-medium">{selectedEvent.user_email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Detected</p>
                  <p className="font-medium">{new Date(selectedEvent.detected_at).toLocaleString()}</p>
                </div>

                {selectedEvent.raw_data && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Raw Data</p>
                    <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-48">{JSON.stringify(selectedEvent.raw_data, null, 2)}</pre>
                  </div>
                )}

                {/* Resolution Form */}
                {(selectedEvent.status === 'new' || selectedEvent.status === 'investigating') && (
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Resolution</p>
                    <textarea
                      value={note}
                      onChange={e => setNote(e.target.value)}
                      placeholder="Add a note..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-3"
                      rows={3}
                    />
                    <div className="flex gap-2">
                      {selectedEvent.status === 'new' && (
                        <button
                          onClick={() => handleAction(selectedEvent.id, 'investigate')}
                          disabled={actionLoading === selectedEvent.id}
                          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          Investigate
                        </button>
                      )}
                      <button
                        onClick={() => handleAction(selectedEvent.id, 'resolve')}
                        disabled={actionLoading === selectedEvent.id}
                        className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                      >
                        Resolve
                      </button>
                      <button
                        onClick={() => handleAction(selectedEvent.id, 'false_positive')}
                        disabled={actionLoading === selectedEvent.id}
                        className="px-3 py-1.5 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                      >
                        False Positive
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
